import { OverviewAccordion } from '../ComponentsOverview/OverviewAccordion';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors } from '@/theme/colors';
import { useFilterReviewActionPois } from '../hooks/useFilterReviewActionPois';
import { feedbackContainerStyles } from '../DetailsGrid/styles';
import { IconButton, Tooltip } from '@mui/material';
import { VIEW_IN_3D_TOOLTIP_LABEL } from '@/constants';
import { useCallback } from 'react';

export const PoiSection = ({
  componentName,
  assemblyId,
}: {
  componentName?: string;
  assemblyId: string;
}) => {
  const { allPois, loading } = useFilterReviewActionPois(assemblyId);
  // Generate headers based on configured fields for each group
  const getHeadersForGroup = useCallback((group: typeof allPois[0]) => {
    // Use headers from the group if available (based on reviewActionTableConfigs)
    if (group.headers && group.headers.length > 0) {
      return group.headers;
    }
    // Fallback: get headers from actual data
    if (!group.data || group.data.length === 0) return [];
    const availableKeys = Array.from(
      new Set(group.data.flatMap((row) => Object.keys(row)))
    );
    return availableKeys;
  }, []);
  return (
    <Stack direction="column" gap="2rem" sx={{ width: '100%' }}>
      {allPois &&
        !loading &&
        allPois.length > 0 &&
        allPois.map((pois, groupIndex) => {
          return (
            <OverviewAccordion
              key={groupIndex}
              title={pois.title}
              componentName={componentName ?? ''}
              defaultExpanded={pois.data.length > 0}
              details={
                <>
                  {pois.data.length > 0 ? (
                    <Grid
                      container
                      direction="column"
                      sx={{ width: '100%', p: '4rem', pt: '1.4rem' }}
                    >
                      <Grid
                        size={{ xs: 12 }}
                        sx={{ border: `0.1rem solid ${abyssColors.primary[100]}` }}
                      >
                        <Grid container>
                          {(() => {
                            const groupHeaders = getHeadersForGroup(pois);
                            return groupHeaders.map((header, index) => (
                              <Grid
                                key={`header-${index}`}
                                size={{ xs: 12 / groupHeaders.length }}
                                sx={{
                                  px: '0.8rem',
                                  py: '0.8rem',
                                  borderRight:
                                    index !== groupHeaders.length - 1
                                      ? `0.1rem solid ${abyssColors.primary[100]}`
                                      : 'none',
                                  borderBottom: `0.1rem solid ${abyssColors.primary[100]}`,
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '1.2rem',
                                    color: abyssColors.primary[500],
                                  }}
                                >
                                  {header}
                                </Typography>
                              </Grid>
                            ));
                          })()}
                        </Grid>

                        {pois.data.map((row, rowIndex) => (
                          <Grid
                            container
                            key={`row-${rowIndex}`}
                            sx={{
                              borderBottom:
                                rowIndex !== pois.data.length - 1
                                  ? `0.1rem solid ${abyssColors.primary[100]}`
                                  : 'none',
                            }}
                          >
                              {(() => {
                                const groupHeaders = getHeadersForGroup(pois);
                                return groupHeaders.map((key, colIndex) => {
                                  const cellData = row[key as keyof typeof row];
                                  const hasDisplayConfig = cellData && 'displayConfig' in cellData;
                                  const is3DButton =
                                    hasDisplayConfig && cellData.displayConfig?.type === '3DBUTTON';
                                  const hasOnClick = cellData && 'onClick' in cellData;

                              return (
                                <Grid
                                  key={`cell-${rowIndex}-${colIndex}`}
                                  size={{ xs: 12 / groupHeaders.length }}
                                  sx={{
                                    px: '0.8rem',
                                    py: '0.8rem',
                                    borderRight:
                                      colIndex !== groupHeaders.length - 1
                                        ? `0.1rem solid ${abyssColors.primary[100]}`
                                        : 'none',
                                  }}
                                >
                                  {is3DButton && hasOnClick ? (
                                    <Tooltip
                                      title={VIEW_IN_3D_TOOLTIP_LABEL}
                                      arrow
                                      placement="right"
                                    >
                                      <span>
                                        <IconButton
                                          onClick={cellData.onClick}
                                          sx={{
                                            color: abyssColors.primary[500],
                                            '&:hover': {
                                              backgroundColor: abyssColors.primary[50],
                                            },
                                          }}
                                        >
                                          <AbyssIcon iconName="cube-secondary" size="md" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 400,
                                        fontSize: '1.2rem',
                                        color: abyssColors.primary[500],
                                      }}
                                    >
                                             {!is3DButton && cellData && 'value' in cellData ? (
                                          (cellData as { isLink?: boolean }).isLink ? (
                                            <a
                                              href={cellData.value}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              title={cellData.value}
                                              style={{
                                                color: abyssColors.primary[500],
                                                textDecoration: 'underline',
                                              }}
                                            >
                                              POI Link
                                            </a>
                                          ) : (
                                            cellData.value
                                          )
                                        ) : (
                                          ''
                                        )}
                                      </Typography>
                                    )}
                                  </Grid>
                                );
                              });
                            })()}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={feedbackContainerStyles} data-testid="feedback-circle">
                      <FeedbackCircle
                        backgroundColor="none"
                        text="No data available to show"
                        textColor={abyssColors.primary[300]}
                        icon={<AbyssIcon iconName="fallback-component-overview" size="2xl" />}
                      />
                    </Box>
                  )}
                </>
              }
            />
          );
        })}
    </Stack>
  );
};
